import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Cube, Images, MagnifyingGlass, MagnifyingGlassPlus, SortAscending, X } from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { PortalLightbox } from "@/components/PortalLightbox";
import { Slab3D } from "@/components/site/Slab3D";
import { SplitText } from "@/components/site/Motion";
import { InventoryFilters } from "@/components/site/InventoryFilters";
import { Pager, ShopToolbar, scrollToTopOf } from "@/components/site/Shop";
import { FAMILIES, familiesOf } from "@/lib/colorFamily";
import { portalApi, portalAsset, itemSlug } from "@/lib/portalApi";
import { useGridCols } from "@/lib/useGridCols";

const PAGE = 12;
const EMPTY = { brand: [], color: [], finish: [], material: [], type: [], thickness: [], price: [] };
const RATIOS = ["aspect-[4/5]"];

// Price-per-ft² buckets for the side filter — only shown when the account may see prices.
const PRICE_BUCKETS = [
  { id: "u30", label: "Under $30", test: (p) => p < 30 },
  { id: "30-50", label: "$30 – $50", test: (p) => p >= 30 && p < 50 },
  { id: "50-75", label: "$50 – $75", test: (p) => p >= 50 && p < 75 },
  { id: "75-100", label: "$75 – $100", test: (p) => p >= 75 && p < 100 },
  { id: "100p", label: "$100 & up", test: (p) => p >= 100 },
];

const round = (x) => (Number.isInteger(x) ? x : Math.round(x));
const dimsOf = (it) =>
  [it.length_in > 0 && it.width_in > 0 ? `${round(it.length_in)}" × ${round(it.width_in)}"` : "Size on request", it.thickness, it.finish]
    .filter(Boolean)
    .join(" · ");
const stockOf = (it) => (it.requested ? "Requested" : it.kind === "Remnant" ? "Remnant" : it.qty > 1 ? `${it.qty} in stock` : "1 in stock");

const tally = (values) => {
  const counts = new Map();
  values.filter(Boolean).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  return [...counts.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
};

const photoUrl = (p, w = 900) => portalAsset(`/inventory/photo/${p}?w=${w}`);

// Remnants carry a traced outline — draw it so the odd shape reads at a glance in the tile.
const polyPoints = (it) =>
  (it.polygon || [])
    .map((p) => [Number(p.x ?? p[0] ?? 0), Number(p.y ?? p[1] ?? 0)])
    .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));

const RemnantOutline = ({ it, testId }) => {
  const pts = polyPoints(it);
  if (pts.length < 3) return null;
  const L = Number(it.length_in) || 1;
  const W = Number(it.width_in) || 1;
  const H = Math.max(20, Math.min(100, (W / L) * 100));
  return (
    <div className="flex h-full w-full items-center justify-center p-6 text-[color:var(--s-mute)]">
      <svg
        viewBox={`-6 -6 112 ${H + 12}`}
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full"
        data-testid={testId}
      >
        <rect x="0" y="0" width="100" height={H} fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="0.7" strokeDasharray="3 2" />
        <polygon
          points={pts.map(([x, y]) => `${x * 100},${y * H}`).join(" ")}
          fill="rgba(195,158,109,0.18)"
          stroke="#c39e6d"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

const hex = (c) => {
  if (typeof c !== "string") return null;
  const t = c.trim();
  return /^#?[0-9a-fA-F]{6}$/.test(t) ? (t.startsWith("#") ? t : `#${t}`) : null;
};
const slabSwatches = (it) => [it.primary_color, it.accent_color].map(hex).filter(Boolean);

/* Portfolio tile mirroring the public showroom — caption overlay, hold / details actions. */
const Tile = ({ it, list, onGallery, onOpen, on3d, showPrice, backdrop, ratio }) => {
  const priceStr = showPrice && it.price_per_sqft != null ? `$${it.price_per_sqft.toFixed(2)}/ft²` : null;
  const metaStr = it.sqft ? `${it.sqft} ft²` : it.thickness || it.kind;
  const hasImg = !!it.photo;
  const remnant = it.kind === "Remnant" && polyPoints(it).length >= 3 && !hasImg;
  const details = (
    <button
      type="button"
      data-testid={`portal-details-btn-${it.id}`}
      onClick={() => onOpen(it)}
      className={`s-shop-btn !relative ${list ? "!h-11 flex-none px-5" : "!h-11 px-5"}`}
    >
      <ArrowUpRight size={14} weight="bold" /> More Details
    </button>
  );

  if (list) {
    return (
      <article data-testid={`portal-inventory-item-${it.id}`} className="s-shop-card group grid grid-cols-[8rem_1fr] gap-5 border-b border-[color:var(--s-line)] pb-8 sm:grid-cols-[15rem_1fr] sm:gap-8">
        <div className={`s-fig relative aspect-[399/449] ${backdrop ? "s-cutout-stage" : "bg-[color:var(--s-line)]"}`}>
          {remnant ? (
            <button type="button" data-testid={`portal-inventory-photo-${it.id}`} onClick={() => (it.photo ? onGallery(it) : onOpen(it))} className="block h-full w-full">
              <RemnantOutline it={it} testId={`portal-remnant-shape-${it.id}`} />
            </button>
          ) : it.photo ? (
            <button type="button" data-testid={`portal-inventory-photo-${it.id}`} onClick={() => onGallery(it)} className="block h-full w-full cursor-zoom-in">
              <img src={photoUrl(it.thumb || it.photo)} alt={it.color} loading="lazy" className={backdrop ? "h-full w-full object-contain p-5" : "h-full w-full object-cover"} />
            </button>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[color:var(--s-mute)]">Photo coming soon</div>
          )}
          <span className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            {it.on_sale && <span data-testid={`portal-badge-sale-${it.id}`} className="s-badge s-badge-gold">Sale</span>}
            {it.new_arrival && <span data-testid={`portal-badge-new-${it.id}`} className="s-badge">New</span>}
            {it.kind === "Remnant" && <span className="s-badge">Remnant</span>}
            {it.requested && <span data-testid={`portal-badge-requested-${it.id}`} className="s-badge s-badge-light">Requested</span>}
          </span>
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="s-micro !text-[10px] text-[color:var(--s-mute)]">{[it.brand, it.material].filter(Boolean).join(" · ") || it.kind}</div>
              <button type="button" data-testid={`portal-inventory-name-${it.id}`} onClick={() => onOpen(it)} className="mt-2 block max-w-full truncate text-left text-2xl font-medium tracking-tight text-[color:var(--s-ink)] transition-colors duration-200 hover:text-[color:var(--s-gold)]">
                {it.color || "Unnamed"}
              </button>
              <div className="mt-1.5 text-sm text-[color:var(--s-mute)]" data-testid={`portal-inventory-spec-${it.id}`}>{dimsOf(it)}</div>
            </div>
            <div className="shrink-0 text-right">
              <div className={`text-sm ${it.requested ? "font-semibold text-emerald-500" : "text-[color:var(--s-mute)]"}`} data-testid={`portal-inventory-stock-${it.id}`}>{stockOf(it)}</div>
              {priceStr && <div className="s-num text-xl text-[color:var(--s-gold)]" data-testid={`portal-price-${it.id}`}>{priceStr}</div>}
              <div className={`s-num ${priceStr ? "text-xs text-[color:var(--s-mute)]" : "text-xl text-[color:var(--s-ink)]"}`} data-testid={`portal-inventory-sqft-${it.id}`}>{metaStr}</div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            {details}
          </div>
        </div>
      </article>
    );
  }

  const photos = it.photos || [];
  const swatches = slabSwatches(it);
  return (
    <article data-testid={`portal-inventory-item-${it.id}`} className="group flex flex-col overflow-hidden rounded-2xl bg-[#1a1a18] transition-transform duration-300 hover:-translate-y-1">
      <div className={`relative ${ratio} ${backdrop ? "s-cutout-stage" : "bg-[color:var(--s-line)]"}`}>
        {remnant ? (
          <button type="button" data-testid={`portal-inventory-photo-${it.id}`} onClick={() => (it.photo ? onGallery(it) : onOpen(it))} className="block h-full w-full">
            <RemnantOutline it={it} testId={`portal-remnant-shape-${it.id}`} />
          </button>
        ) : it.photo ? (
          <button type="button" data-testid={`portal-inventory-photo-${it.id}`} onClick={() => onGallery(it)} className="block h-full w-full cursor-zoom-in">
            <img src={photoUrl(it.thumb || it.photo)} alt={it.color} loading="lazy" className={backdrop ? "h-full w-full object-contain p-6" : "h-full w-full object-cover"} />
          </button>
        ) : (
          <button type="button" onClick={() => onOpen(it)} className="flex h-full w-full items-center justify-center text-sm text-[color:var(--s-mute)]">Photo coming soon</button>
        )}

        {it.new_arrival && (
          <span data-testid={`portal-badge-new-${it.id}`} className="absolute left-4 top-0 z-[4] bg-white px-2.5 py-4 text-[9px] font-bold uppercase tracking-[0.3em] text-[#1a1a18] [writing-mode:vertical-rl] rotate-180">
            New
          </span>
        )}
        <span className="absolute right-3 top-3 z-[3] flex flex-col items-end gap-1.5">
          {it.on_sale && <span data-testid={`portal-badge-sale-${it.id}`} className="s-badge s-badge-gold">Sale</span>}
          {it.kind === "Remnant" && <span className="s-badge s-badge-light">Remnant</span>}
          {it.requested && <span data-testid={`portal-badge-requested-${it.id}`} className="s-badge s-badge-light">Requested</span>}
        </span>

        <span className="absolute bottom-3 right-3 z-[3] flex gap-1.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 focus-within:opacity-100">
          {it.photo && (
            <button type="button" data-testid={`portal-3d-btn-${it.id}`} onClick={() => on3d(it)} title="3D view" className="flex h-10 w-10 items-center justify-center rounded-md bg-[#c39e6d] text-[#1a1a18] shadow-md ring-1 ring-black/10 transition-colors duration-300 hover:bg-[#b4895a]">
              <Cube size={16} weight="bold" />
            </button>
          )}
          <button type="button" data-testid={`portal-gallery-btn-${it.id}`} disabled={!photos.length} onClick={() => onGallery(it)} title="Photos" className="flex h-10 w-10 items-center justify-center rounded-md bg-[#c39e6d] text-[#1a1a18] shadow-md ring-1 ring-black/10 transition-colors duration-300 hover:bg-[#b4895a] disabled:opacity-30">
            <Images size={16} weight="bold" />
          </button>
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5" data-testid={`portal-tile-caption-${it.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="s-micro !text-[10px] text-white/55">{[it.brand, it.material].filter(Boolean).join(" · ") || it.kind}</div>
            <button type="button" data-testid={`portal-inventory-name-${it.id}`} onClick={() => onOpen(it)} className="mt-1.5 block max-w-full truncate text-left text-xl font-medium tracking-tight text-white transition-colors duration-200 hover:text-[color:var(--s-gold-bright)]">
              {it.color || "Unnamed"}
            </button>
            <div className="mt-1.5 truncate text-[11px] text-white/45" data-testid={`portal-inventory-spec-${it.id}`}>{dimsOf(it)}</div>
          </div>
          <div className="shrink-0 text-right">
            {priceStr ? (
              <div className="s-num whitespace-nowrap text-xl leading-none text-white" data-testid={`portal-price-${it.id}`}>{priceStr}</div>
            ) : (
              <div className="s-num whitespace-nowrap text-lg leading-none text-white/80" data-testid={`portal-inventory-sqft-${it.id}`}>{metaStr}</div>
            )}
            {priceStr && <div className="mt-1 whitespace-nowrap text-[11px] text-white/45" data-testid={`portal-inventory-sqft-${it.id}`}>{metaStr}</div>}
          </div>
        </div>
        <div className="mt-5 flex items-end justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1.5">
            {swatches.map((c, n) => (
              <span key={n} className="h-5 w-5 rounded-full ring-1 ring-white/25" style={{ background: c }} />
            ))}
            <span data-testid={`portal-inventory-stock-${it.id}`} className={`truncate text-[11px] ${it.requested ? "font-semibold text-emerald-400" : "text-white/45"}`}>{stockOf(it)}</span>
          </div>
          <button type="button" data-testid={`portal-details-btn-${it.id}`} onClick={() => onOpen(it)} className="group/btn inline-flex h-11 shrink-0 items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:text-[color:var(--s-gold-bright)]">
            More Details <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </article>
  );
};

export default function PortalInventory() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [pricing, setPricing] = useState({ show: false, label: "" });
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const fromParams = () => ({
    ...EMPTY,
    material: params.get("material") ? [params.get("material")] : [],
    type: params.get("type") ? [params.get("type")] : [],
  });
  const [picked, setPicked] = useState(fromParams);
  const [panel, setPanel] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("portal-inventory-view") || "grid");
  const { cols, choose, max: maxCols } = useGridCols("portal-inventory-cols", 3);
  const [sort, setSort] = useState("featured");
  const [page, setPage] = useState(1);
  const gridRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!panel) return undefined;
    const onKey = (e) => e.key === "Escape" && setPanel(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel]);

  useEffect(() => {
    portalApi
      .get("/inventory")
      .then(({ data }) => {
        setItems(data.items || []);
        setPricing(data.pricing || { show: false, label: "" });
        setCatalog(data.catalog || {});
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => localStorage.setItem("portal-inventory-view", view), [view]);
  useEffect(() => setPicked(fromParams()), [params]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const facets = useMemo(() => {
    const colorCounts = new Map();
    items.forEach((i) => familiesOf(i).forEach((f) => colorCounts.set(f, (colorCounts.get(f) || 0) + 1)));
    return {
      brand: tally(items.map((i) => i.brand)),
      color: FAMILIES.filter(([name]) => colorCounts.get(name)).map(([name]) => [name, colorCounts.get(name)]),
      finish: tally(items.map((i) => i.finish)),
      material: tally(items.map((i) => i.material)),
      type: tally(items.map((i) => i.kind)),
      thickness: tally(items.map((i) => i.thickness)),
      price: pricing.show
        ? PRICE_BUCKETS.map((b) => ({
            id: b.id,
            label: b.label,
            count: items.filter((i) => i.price_per_sqft != null && b.test(i.price_per_sqft)).length,
          })).filter((b) => b.count > 0)
        : [],
    };
  }, [items, pricing.show]);

  const toggle = (group, value) =>
    setPicked((p) => ({ ...p, [group]: p[group].includes(value) ? p[group].filter((x) => x !== value) : [...p[group], value] }));
  const clearAll = () => {
    setPicked({ ...EMPTY });
    navigate("/portal/inventory", { replace: true });
  };
  const activeCount = Object.values(picked).reduce((n, v) => n + v.length, 0);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hit = (list, value) => !list.length || list.includes(value);
    const priceHit = (it) =>
      !picked.price.length ||
      (it.price_per_sqft != null && PRICE_BUCKETS.some((b) => picked.price.includes(b.id) && b.test(it.price_per_sqft)));
    const out = items.filter(
      (it) =>
        hit(picked.brand, it.brand) &&
        hit(picked.finish, it.finish) &&
        hit(picked.material, it.material) &&
        hit(picked.type, it.kind) &&
        hit(picked.thickness, it.thickness) &&
        priceHit(it) &&
        (!picked.color.length || familiesOf(it).some((f) => picked.color.includes(f))) &&
        (needle ? [it.color, it.material, it.finish, it.thickness, it.brand].filter(Boolean).join(" ").toLowerCase().includes(needle) : true),
    );
    if (sort === "name") out.sort((a, b) => (a.color || "").localeCompare(b.color || ""));
    else if (sort === "sqft") out.sort((a, b) => (b.sqft || 0) - (a.sqft || 0));
    else if (sort === "price") out.sort((a, b) => (a.price_per_sqft ?? 1e9) - (b.price_per_sqft ?? 1e9));
    else if (sort === "price-desc") out.sort((a, b) => (b.price_per_sqft ?? -1) - (a.price_per_sqft ?? -1));
    return out;
  }, [items, q, picked, sort]);

  useEffect(() => setPage(1), [q, picked, sort]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const visible = useMemo(() => shown.slice((page - 1) * PAGE, page * PAGE), [shown, page]);
  const goPage = (n) => {
    setPage(Math.min(pages, Math.max(1, n)));
    scrollToTopOf(gridRef.current);
  };

  const [galleryId, setGalleryId] = useState(null);
  const gallery = useMemo(() => {
    const it = items.find((x) => x.id === galleryId);
    if (!it) return [];
    return (it.photos || []).map((p, n) => ({
      path: p.path,
      thumb: p.thumb,
      title: it.color || "Unnamed",
      sub: `${it.kind} · ${it.size}${it.thickness ? ` · ${it.thickness}` : ""}${(it.photos || []).length > 1 ? ` · photo ${n + 1} of ${it.photos.length}` : ""}`,
    }));
  }, [items, galleryId]);
  const [light, setLight] = useState(-1);
  const [view3dItem, setView3dItem] = useState(null);
  const openGallery = (it) => {
    if (!(it.photos || []).length) return;
    setGalleryId(it.id);
    setLight(0);
  };
  const openItem = (it) => navigate(`/portal/inventory/${itemSlug(it.kind, it.color, it.id)}`);

  const sorts = [
    ["featured", "Default sorting"],
    ["name", "Sort by name: A to Z"],
    ["sqft", "Sort by size: largest first"],
    ...(pricing.show ? [["price", "Sort by price: low to high"], ["price-desc", "Sort by price: high to low"]] : []),
  ];

  const materialChips = [["All inventory", ""], ...facets.material.map(([m, c]) => [m, m, c])];

  const heading = picked.type.includes("Remnant")
    ? "Remnants."
    : picked.material.length === 1
      ? `${picked.material[0]}.`
      : "Inventory.";

  return (
    <PortalShell>
      <div className="site -mx-6 bg-[color:var(--s-bg)] px-6 pb-16 pt-2 lg:-mx-12 lg:px-12" data-testid="portal-inventory">
        {/* Nordic portfolio header, mirroring the public showroom */}
        <div className="flex flex-col gap-6 pt-6 lg:flex-row lg:items-end lg:justify-between" data-testid="portal-inventory-hero">
          <div className="max-w-3xl">
            <span className="s-eyebrow">Live stock</span>
            <h1 className="s-hx s-spaced mt-5 break-words text-5xl text-[color:var(--s-ink)] sm:text-6xl lg:text-7xl" data-testid="portal-inventory-headline">
              <SplitText text={heading} />
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)]">
              Every slab and remnant sitting on our racks right now, photographed as it landed. Filter it down, then ask us to hold the piece you like.
            </p>
          </div>
          <p className="inline-flex items-center gap-3 whitespace-nowrap text-sm text-[color:var(--s-ink)]" data-testid="portal-inventory-total">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--s-gold-bright)] opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--s-gold-bright)]" />
            </span>
            <span className="s-num text-2xl">{items.length}</span>
            <span className="text-[color:var(--s-mute)]">pieces available today</span>
          </p>
        </div>

        {/* category chips + search toggle */}
        <div className="mt-10 flex flex-col gap-4 border-y border-[color:var(--s-line)] py-4 lg:flex-row lg:items-center lg:justify-between" data-testid="portal-inventory-categories">
          <div className="no-bar -mx-1 flex gap-x-6 gap-y-2 overflow-x-auto px-1">
            {materialChips.map(([label, val, count]) => {
              const on = val ? picked.material.length === 1 && picked.material[0] === val : picked.material.length === 0;
              const cnt = val ? count : items.length;
              return (
                <button
                  key={val || "__all__"}
                  type="button"
                  aria-pressed={on}
                  data-testid={`portal-inventory-cat-${val ? val.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "all"}`}
                  onClick={() => setPicked((p) => ({ ...p, material: val ? [val] : [], type: [] }))}
                  className={`s-micro flex shrink-0 items-center gap-2 whitespace-nowrap py-2 !text-[12px] tracking-[0.1em] transition-colors duration-300 ${on ? "text-[color:var(--s-ink)]" : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${on ? "bg-[color:var(--s-gold-bright)]" : "bg-transparent"}`} />
                  {label}
                  <span className="text-[color:var(--s-mute)]">({cnt})</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            data-testid="portal-inventory-search-btn"
            onClick={() => setSearchOpen((v) => !v)}
            aria-expanded={searchOpen}
            className="s-micro inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-[color:var(--s-line)] px-5 py-2.5 !text-[11px] text-[color:var(--s-ink)] transition-colors duration-300 hover:border-[color:var(--s-gold-bright)] hover:bg-[color:var(--s-gold-bright)] hover:text-[#1a1a18] lg:self-auto"
          >
            {searchOpen ? <X size={14} weight="bold" /> : <MagnifyingGlass size={14} weight="bold" />}
            {searchOpen ? "Close search" : "Search"}
          </button>
        </div>

        <div className={`grid transition-all duration-500 ${searchOpen ? "mt-6 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
          <div className="overflow-hidden">
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-[color:var(--s-mute)]">
                <MagnifyingGlass size={18} weight="bold" />
              </span>
              <input
                ref={searchRef}
                data-testid="portal-inventory-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a color, material or brand"
                className="h-14 w-full border border-[color:var(--s-line)] bg-[color:var(--s-surface)] pl-12 pr-12 text-base text-[color:var(--s-ink)] outline-none transition-colors duration-200 focus:border-[color:var(--s-ink)] placeholder:text-[color:var(--s-mute)]"
              />
              {q && (
                <button type="button" onClick={() => setQ("")} aria-label="Clear search" data-testid="portal-inventory-search-clear" className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]">
                  <X size={16} weight="bold" />
                </button>
              )}
            </div>
          </div>
        </div>

        {pricing.show && (
          <div className="mt-6 flex flex-wrap items-center gap-3 border-l-2 border-[color:var(--s-gold-bright)] bg-[#f7f1e3] px-4 py-3" data-testid="portal-pricing-note">
            <span className="s-micro !text-[10px] text-[color:var(--s-gold)]">{pricing.label}</span>
            <span className="text-xs text-[color:var(--s-mute)]">Prices are per square foot of material and exclude fabrication and installation.</span>
            <button
              type="button"
              data-testid="portal-sort-lowest-price"
              onClick={() => {
                setSort((s) => (s === "price" ? "featured" : "price"));
                scrollToTopOf(gridRef.current);
              }}
              className={`s-micro ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 !text-[10px] transition-colors duration-200 ${
                sort === "price"
                  ? "border-[color:var(--s-gold)] bg-[color:var(--s-gold-bright)] text-[#1a1a18]"
                  : "border-[color:var(--s-gold-bright)] text-[color:var(--s-gold)] hover:bg-[color:var(--s-gold-bright)] hover:text-[#1a1a18]"
              }`}
            >
              <SortAscending size={13} weight="bold" />
              {sort === "price" ? "Lowest price first" : "Show lowest price first"}
            </button>
          </div>
        )}

        {/* left persistent filter sidebar + grid */}
        <div className="mt-8 lg:grid lg:grid-cols-[280px_1fr] lg:gap-10 xl:gap-14" ref={gridRef}>
          <aside className="hidden lg:block" data-testid="portal-inventory-sidebar">
            <div className="sticky top-28">
              <InventoryFilters facets={facets} picked={picked} toggle={toggle} clear={clearAll} activeCount={activeCount} />
            </div>
          </aside>
          <div className="min-w-0">
            <ShopToolbar
              prefix="portal-inventory"
              filterTestId="portal-inventory-toggle-filters"
              filterCountTestId="portal-filters-count"
              start={shown.length ? (page - 1) * PAGE + 1 : 0}
              end={Math.min(page * PAGE, shown.length)}
              total={shown.length}
              extra={
                activeCount > 0 && (
                  <button type="button" data-testid="portal-filters-clear" onClick={clearAll} className="s-micro inline-flex items-center gap-2 !text-[10px] text-[color:var(--s-mute)] transition-colors duration-200 hover:text-[color:var(--s-ink)]">
                    Clear {activeCount} filter{activeCount === 1 ? "" : "s"} <X size={12} weight="bold" />
                  </button>
                )
              }
              sort={sort}
              onSort={setSort}
              sortOptions={sorts}
              view={view}
              onView={setView}
              cols={cols}
              onCols={choose}
              maxCols={maxCols}
              panel={panel}
              onPanel={() => setPanel((v) => !v)}
              activeCount={activeCount}
              filterAlways={false}
            />

            {loading && (
              <div className="mt-10 grid gap-6" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="aspect-[4/5] animate-pulse bg-[color:var(--s-line)]" />
                ))}
              </div>
            )}

            {!loading && shown.length > 0 && (
              view === "list" ? (
                <div className="mt-10 flex flex-col gap-8" data-testid="portal-inventory-grid" data-view="list">
                  {visible.map((it) => (
                    <Tile key={it.id} it={it} list onGallery={openGallery} onOpen={openItem} on3d={setView3dItem} showPrice={pricing.show} backdrop={!!catalog.cutout_backdrop && !!it.cutout} />
                  ))}
                </div>
              ) : (
                <div className="mt-10 grid gap-6" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="portal-inventory-grid" data-view="grid" data-cols={cols}>
                  {visible.map((it, n) => (
                    <Tile key={it.id} it={it} list={false} ratio={RATIOS[n % RATIOS.length]} onGallery={openGallery} onOpen={openItem} on3d={setView3dItem} showPrice={pricing.show} backdrop={!!catalog.cutout_backdrop && !!it.cutout} />
                  ))}
                </div>
              )
            )}

            <Pager page={page} pages={pages} onPage={goPage} prefix="portal-inventory" />

            {!loading && shown.length === 0 && (
              <div className="mt-10 flex flex-col items-center border border-dashed border-[color:var(--s-line)] px-6 py-16 text-center sm:py-24" data-testid="portal-inventory-empty">
                <h3 className="s-hx text-4xl sm:text-5xl">Nothing to show.</h3>
                <p className="mt-5 max-w-sm text-base leading-relaxed text-[color:var(--s-mute)]">
                  Nothing matches that search right now — call us and we&apos;ll tell you what is landing next.
                </p>
                {activeCount > 0 && (
                  <button type="button" onClick={clearAll} className="s-ghost mt-10">Clear filters</button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* mobile filter drawer */}
      <div className={`site fixed inset-0 z-[80] overflow-hidden !bg-transparent ${panel ? "" : "pointer-events-none"}`} aria-hidden={!panel} inert={panel ? undefined : true} data-testid="portal-filter-root">
        <button type="button" aria-label="Close filters" onClick={() => setPanel(false)} data-testid="portal-filter-backdrop" className={`absolute inset-0 bg-[#1a1a18]/50 transition-opacity duration-500 ${panel ? "opacity-100" : "opacity-0"}`} />
        <aside data-testid="portal-inventory-filters" className={`no-bar absolute inset-y-0 right-0 flex w-full max-w-[440px] flex-col overflow-y-auto bg-[color:var(--s-bg)] px-8 pb-12 pt-7 text-[color:var(--s-ink)] shadow-[-40px_0_80px_-40px_rgba(0,0,0,0.5)] transition-transform duration-600 ease-[cubic-bezier(.2,.7,.2,1)] sm:px-10 ${panel ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between">
            <span className="s-micro text-[color:var(--s-mute)]">{shown.length} {shown.length === 1 ? "result" : "results"}</span>
            <button type="button" onClick={() => setPanel(false)} data-testid="portal-filter-close" className="s-micro flex items-center gap-2 transition-colors duration-300 hover:text-[color:var(--s-gold)]">
              Close <X size={16} weight="bold" />
            </button>
          </div>
          <h2 className="s-hx mt-10 text-4xl" data-testid="portal-filter-title">Filter <strong>stone.</strong></h2>
          <InventoryFilters facets={facets} picked={picked} toggle={toggle} clear={clearAll} activeCount={activeCount} />
          <div className="mt-10 grid gap-4">
            <button type="button" data-testid="portal-filter-apply" onClick={() => setPanel(false)} className="s-ghost w-full">Show {shown.length} {shown.length === 1 ? "slab" : "slabs"}</button>
            {activeCount > 0 && (
              <button type="button" data-testid="portal-filter-reset" onClick={clearAll} className="s-micro text-center !text-[11px] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]">Clear all filters</button>
            )}
          </div>
        </aside>
      </div>

      {view3dItem && (
        <Slab3D
          photo={view3dItem.photo ? photoUrl(view3dItem.photo, 2400) : ""}
          lengthIn={view3dItem.length_in}
          widthIn={view3dItem.width_in}
          thickness={view3dItem.thickness}
          title={view3dItem.color}
          onClose={() => setView3dItem(null)}
        />
      )}
      {light >= 0 && gallery.length > 0 && (
        <PortalLightbox shots={gallery} index={light} srcOf={(path) => portalAsset(`/inventory/photo/${path}`)} onClose={() => setLight(-1)} />
      )}
    </PortalShell>
  );
}
